import {
  addMonths,
  format,
  isAfter,
  isBefore,
  isSameMonth,
  subMonths
} from 'date-fns'
import {
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import {
  GUEST_WEEK_STARTS_ON,
  buildMonthGrid
} from './schedulingUtils'

const SUNDAY_FIRST = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const WEEKDAYS = [
  ...SUNDAY_FIRST.slice(GUEST_WEEK_STARTS_ON),
  ...SUNDAY_FIRST.slice(0, GUEST_WEEK_STARTS_ON)
]

export function MonthCalendar({
  availableDateKeys,
  selectedDateKey,
  onSelectDay,
  monthCursor,
  onMonthChange,
  minMonth,
  maxMonth
}: {
  availableDateKeys: Set<string>,
  selectedDateKey: string | null,
  onSelectDay: (dateKey: string) => void,
  monthCursor: Date,
  onMonthChange: (month: Date) => void,
  minMonth: Date,
  maxMonth: Date
}): JSX.Element {
  const { leadingBlanks, days } = buildMonthGrid(monthCursor, GUEST_WEEK_STARTS_ON)
  const canGoPrev = isAfter(monthCursor, minMonth) && !isSameMonth(monthCursor, minMonth)
  const canGoNext = isBefore(monthCursor, maxMonth) && !isSameMonth(monthCursor, maxMonth)

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-base font-medium text-gray-900">
          {format(monthCursor, 'MMMM yyyy')}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            aria-label="Previous month"
            disabled={!canGoPrev}
            onClick={() => onMonthChange(subMonths(monthCursor, 1))}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label="Next month"
            disabled={!canGoNext}
            onClick={() => onMonthChange(addMonths(monthCursor, 1))}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((day) => (
          <div key={day} className="text-center text-xs text-gray-400 font-medium py-1">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`blank-${i}`} />
        ))}
        {days.map(({ date, dateKey }) => {
          const isAvailable = availableDateKeys.has(dateKey)
          const isSelected = dateKey === selectedDateKey
          if (!isAvailable) {
            return (
              <div key={dateKey} className="flex justify-center">
                <span className="w-10 h-10 flex items-center justify-center text-sm text-gray-300">
                  {date.getDate()}
                </span>
              </div>
            )
          }
          return (
            <div key={dateKey} className="flex justify-center">
              <button
                type="button"
                aria-label={format(date, 'EEEE, MMMM d, yyyy')}
                aria-pressed={isSelected}
                onClick={() => onSelectDay(dateKey)}
                className={[
                  'w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-colors',
                  isSelected
                    ? 'bg-primary-500 text-white'
                    : 'bg-primary-500/10 text-primary-600 hover:bg-primary-500/20'
                ].join(' ')}
              >
                {date.getDate()}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
